import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Output } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LetDirective } from '@ngrx/component';
import { cloneDeep, isNil } from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { AllDataUser, DeleteUserRequest, PossibleRole, RolesSchema } from '@pwp-common';

import { USLocalPhoneValidator } from '../../../../common/validators/us-local-phone-validator';
import { AdminRolesService } from '../../../../services/user/admin-roles/admin-roles.service';
import { AllDataUserService } from '../../../../services/user/all-data-user/all-data-user.service';
import { DeleteUserService } from '../../../../services/user/delete-user/delete-user.service';
import { UserEndpointService } from '../../../../services/user/user-endpoint/user-endpoint.service';
import { FormFieldHelpComponent } from '../../../form/form-field-help/form-field-help.component';
import { ComponentWithEmittingForm } from '../../../generic/abstract-classes/component-with-emitting-form';
import { ConfirmWithInputComponent } from '../../../generic/confirm-with-input/confirm-with-input.component';
import { ChangeOtherUserEmailComponent } from '../change-other-user-email/change-other-user-email.component';
import { ChangeOtherUserEmailDialogInput } from '../change-other-user-email/interfaces';
import { ChangeOtherUserPasswordComponent } from '../change-other-user-password/change-other-user-password.component';
import { ChangeOtherUserPasswordDialogInput } from '../change-other-user-password/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-all-data-user-editor',
  templateUrl: './all-data-user-editor.component.html',
  styleUrls: ['./all-data-user-editor.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LetDirective,
    TranslocoModule,
    ButtonModule,
    CardModule,
    ConfirmDialogModule,
    ToastModule,
    InputTextModule,
    InputMaskModule,
    MultiSelectModule,
    InputTextareaModule,
    FormFieldHelpComponent,
  ],
})
export class AllDataUserEditorComponent extends ComponentWithEmittingForm<AllDataUser> {
  public uploadProcessing = false;

  public deleteProcessing = false;

  public deleteDialogOpened = false;

  public loggedInUserIsOrgAdmin: boolean;

  public loggedInUserIsPwpAdmin: boolean;

  public disableOrEnableProcessing = false;

  public readonly rolesOptions$ = this.getAllRolesOption();

  private readonly dialogService = inject(DialogService);

  ////////////////////////////////////////////////////////////////////////
  // Emit Changes
  ////////////////////////////////////////////////////////////////////////

  @Output()
  public readonly userIdUpdated = new EventEmitter<string>();

  @Output()
  public readonly cancelEdit = new EventEmitter<void>();

  ////////////////////////////////////////////////////////////////////////
  // Lifecycle
  ////////////////////////////////////////////////////////////////////////

  constructor(
    private adminRolesService: AdminRolesService,
    private deleteUserService: DeleteUserService,
    private allDataUserService: AllDataUserService,
    private userEndpointService: UserEndpointService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fb: NonNullableFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ////////////////////////////////////////////////////////////////////////
  // Get Data
  ////////////////////////////////////////////////////////////////////////

  protected getDataPromise(): Promise<any> {
    const orgAdminPromise = this.allDataUserService
      .getLoggedInData()
      .pipe(
        map((z) => {
          this.loggedInUserIsOrgAdmin = z.roles.isOrgAdmin();
          this.changeDetectorRef.detectChanges();
        }),
      )
      .toPromise();

    const pwpAdminPromise = this.adminRolesService
      .getAdminRoles()
      .pipe(
        map((z) => {
          this.loggedInUserIsPwpAdmin = z.isPwpAdmin();
          this.changeDetectorRef.detectChanges();
        }),
      )
      .toPromise();

    return Promise.all([orgAdminPromise, pwpAdminPromise]);
  }
  ////////////////////////////////////////////////////////////////////////
  // Upload
  ////////////////////////////////////////////////////////////////////////

  public async saveEditUser() {
    if (this.form.invalid || this.isUserDisabled) {
      this.form.markAllAsTouched();
      return;
    }
    this.uploadProcessing = true;
    const obj = this.getObjFromForm();

    try {
      await this.allDataUserService.upload(obj);

      const retreivedUpdatedAllDataUser = await firstValueFrom(this.allDataUserService.getDoc(obj.getUserId()));
      this.obj = cloneDeep(retreivedUpdatedAllDataUser);
    } catch (error) {
      console.error('AllDataUserEditorComponent.upload: Error');
      console.error(error);
      this.uploadProcessing = false;
      // Show Toast
      this.messageService.add({
        severity: 'error',
        summary: 'Error saving profile.',
        detail: JSON.stringify(error),
      });
    }
    this.uploadProcessing = false;
    this.userIdUpdated.emit(this.obj.getUserId());
  }

  ////////////////////////////////////////////////////////////////////////
  // Delete
  ////////////////////////////////////////////////////////////////////////

  public confirmAndDelete() {
    const displayName = this.obj.userData.getDisplayNameWithEmail();
    const email = this.obj.userData.getNotificationEmail();
    const title = translate('all-data-user-editor.confirmDeleteTitle');
    const body = translate('all-data-user-editor.confirmDeleteBody', { displayName, email });

    this.deleteDialogOpened = true;
    this.dialogService
      .open(ConfirmWithInputComponent, {
        header: title,
        width: '80%',
        data: {
          title,
          body,
          requiredConfirmationString: email,
        },
      })
      .onClose.pipe(
        untilDestroyed(this),
        tap(() => {
          this.deleteDialogOpened = false;
        }),
        filter((confirmed) => confirmed === true),
        switchMap(() => this.delete()),
      )
      .subscribe({
        error: (error) => {
          this.deleteDialogOpened = false;
          this.showErrorDialog(error);
        },
      });
  }

  private async delete(): Promise<void> {
    try {
      // Make API request
      const request = new DeleteUserRequest({ userId: this.obj.getUserId() });

      this.deleteProcessing = true;
      this.changeDetectorRef.detectChanges();
      const requestResult = await this.deleteUserService.makeRequest(request);
      if (isNil(requestResult) || !isNil(requestResult.getError())) {
        this.showErrorDialog(requestResult.getError());
      }
    } catch (error) {
      this.showErrorDialog(error);
    } finally {
      this.deleteProcessing = false;
      this.changeDetectorRef.detectChanges();
      this.userIdUpdated.emit(this.obj.getUserId());
    }
  }

  ////////////////////////////////////////////////////////////////////////
  // Messages
  ////////////////////////////////////////////////////////////////////////

  private showErrorDialog(error: string) {
    console.error(`AllDataUserEditorComponent.error: Error delete user API request`);
    console.error(error);
    // Show Toast
    this.messageService.add({
      severity: 'error',
      summary: 'Error deleting profile.',
      detail: JSON.stringify(error),
    });
  }

  ////////////////////////////////////////////////////////////////////////
  // Get Obj From Form
  ////////////////////////////////////////////////////////////////////////

  public getObjFromForm(): AllDataUser {
    const userData = cloneDeep(this.obj.userData);
    userData.setFirstName(this.form.controls.firstName.value);
    userData.setLastInitial(this.form.controls.lastInitial.value);

    const userPrivateData = cloneDeep(this.obj.userPrivateData);
    userPrivateData.setE164Phone(this.form.controls.e164Phone.value);

    const roles = cloneDeep(this.obj.roles);

    roles.setRoles(Array.from(this.form.controls.roles.value));
    roles.setOrgNote(this.form.controls.orgNote.value);

    const obj = new AllDataUser(userData, roles, userPrivateData);
    return obj;
  }

  ////////////////////////////////////////////////////////////////////////
  // Change Password
  ////////////////////////////////////////////////////////////////////////

  public openChangePasswordDialog() {
    if (this.isUserDisabled) {
      return;
    }
    this.dialogService.open(ChangeOtherUserPasswordComponent, {
      width: 'clamp(32rem, 50%, 64rem)',
      data: {
        userId: this.getObjFromForm().getUserId(),
      } as ChangeOtherUserPasswordDialogInput,
      dismissableMask: false,
    });
  }

  ////////////////////////////////////////////////////////////////////////
  // Change Password
  ////////////////////////////////////////////////////////////////////////

  public openChangeEmailDialog() {
    if (this.isUserDisabled) {
      return;
    }
    const dialogRef = this.dialogService.open(ChangeOtherUserEmailComponent, {
      width: 'clamp(32rem, 50%, 64rem)',
      data: {
        userId: this.getObjFromForm().getUserId(),
      } as ChangeOtherUserEmailDialogInput,
      dismissableMask: false,
    });

    dialogRef.onClose
      .pipe(
        filter((result) => result?.success === true),
        switchMap(() => this.allDataUserService.getDoc(this.obj.getUserId())),
        filter((updatedUser): updatedUser is AllDataUser => !!updatedUser),
        tap((updatedUser) => {
          this.obj = cloneDeep(updatedUser);
          this.changeDetectorRef.detectChanges();
        }),
        untilDestroyed(this),
      )
      .subscribe({
        error: (error) => this.showErrorDialog(error),
      });
  }

  ////////////////////////////////////////////////////////////////////////
  // Getters
  ////////////////////////////////////////////////////////////////////////

  public get isUserDisabled(): boolean {
    return this.obj?.roles?.getIsDisabled() ?? false;
  }

  public get disPlayedUsername(): string {
    return `${this.obj.userData.getLastInitial()}. ${this.obj.userData.getFirstName()}`;
  }

  ////////////////////////////////////////////////////////////////////////
  // Disable or Enable User
  ////////////////////////////////////////////////////////////////////////
  public onClickDisableOrEnableUser(): void {
    this.confirmationService.confirm({
      accept: async () => {
        await this.performDisableOrEnableUser();
      },
    });
  }

  private async performDisableOrEnableUser(): Promise<void> {
    try {
      this.disableOrEnableProcessing = true;
      this.changeDetectorRef.detectChanges();

      const newDisabledState = !this.isUserDisabled;

      const result = await this.userEndpointService.updateUser({
        userId: this.obj.getUserId(),
        isDisabled: newDisabledState,
      });

      if (result.getError()) {
        this.showErrorDialog(String(result.getError()));
      } else {
        const updatedUser = await firstValueFrom(this.allDataUserService.getDoc(this.obj.getUserId()));
        if (updatedUser) {
          this.obj = cloneDeep(updatedUser);
          this.refreshFormState();
          this.changeDetectorRef.detectChanges();

          const actionText = newDisabledState ? 'disabled' : 'enabled';
          this.messageService.add({
            severity: 'success',
            summary: translate(`all-data-user-editor.${actionText}Success`, {
              displayName: this.disPlayedUsername,
            }),
          });

          this.userIdUpdated.emit(this.obj.getUserId());
        }
      }
    } catch (error) {
      this.showErrorDialog(String(error));
    } finally {
      this.disableOrEnableProcessing = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  ////////////////////////////////////////////////////////////////////////
  // Get All Roles Option
  ////////////////////////////////////////////////////////////////////////

  private getAllRolesOption() {
    return this.adminRolesService.getAdminRoles().pipe(
      map((userAdminRoles) =>
        this.AVAILABLE_ROLES.map((role) => ({
          label: translate(`all-data-user-editor.roles.${role}.label`),
          description: translate(`all-data-user-editor.roles.${role}.description`),
          disabled: role === RolesSchema.Roles.support && !userAdminRoles.isPwpAdmin(),
          value: role,
        })),
      ),
    );
  }

  private readonly AVAILABLE_ROLES: PossibleRole[] = [
    RolesSchema.Roles.support,
    RolesSchema.Roles.orgAdmin,
    RolesSchema.Roles.getPII,
    RolesSchema.Roles.voicemail,
    RolesSchema.Roles.conversation,
  ];

  ////////////////////////////////////////////////////////////////////////
  // Define Form
  ////////////////////////////////////////////////////////////////////////

  protected defineForm(): void {
    const isDisabled = this.isUserDisabled;

    this.form = this.fb.group({
      firstName: [{ value: this.obj.userData.getFirstName() || '', disabled: isDisabled }, [Validators.required]],
      lastInitial: [{ value: this.obj.userData.getLastInitial() || '', disabled: isDisabled }, [Validators.required]],
      e164Phone: [
        { value: this.obj.userPrivateData.getUSLocalPhone() || '', disabled: isDisabled },
        [Validators.required, USLocalPhoneValidator.usLocalPhoneValidator()],
      ],
      roles: [{ value: this.obj.roles.getRoles() || ([] as PossibleRole[]), disabled: isDisabled }],
      orgNote: [{ value: this.obj.roles.getOrgNote() || '', disabled: isDisabled }, [Validators.maxLength(500)]],
    });
  }

  ////////////////////////////////////////////////////////////////////////
  // Refresh Form State
  ////////////////////////////////////////////////////////////////////////

  private refreshFormState(): void {
    const isDisabled = this.isUserDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
